import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Autocomplete,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
    Chip,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Send as SendIcon,
    PersonSearch as PersonSearchIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    ArrowBack as ArrowBackIcon,
    PhotoCamera as PhotoCameraIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE, GAS_LINE_INSPECTION_STORE, EMPLOYEE_STORE } from '../../../config/constants';
import useDictation from '../../../hooks/useDictation';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import CameraCapture from '../../shared/CameraCapture';
import { generateUUID } from '../../../utils/uuid';
import { hasSignatureContent } from '../../../utils/utils';
import Swal from 'sweetalert2';
import gasLineInspectionService from '../../../services/gasLineInspectionService';

const STATUS_DRAFT = 'draft';
const STATUS_SUBMITTED = 'submitted';

export default function GasLineInspectionForm() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [employees, setEmployees] = useState([]);

    // Get current user info
    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [form, setForm] = useState({
        uuid: generateUUID(),
        jobCode: '',
        jobName: '',
        customer: '',
        location: '',
        testDate: dayjs().format('YYYY-MM-DD'),
        inspectorId: userId,
        inspectorName: userFullName,
        area: '',
        column: '',
        hall: '',
        // Visual Inspection
        visualNotes: '',
        visualPictures: [],
        // Visual Weld Conditions
        weldNotes: '',
        weldPictures: [],
        // Visual Paint Condition
        paintNotes: '',
        paintPictures: [],
        // Ultrasonic Testing
        ultrasonicData: '',
        ultrasonicNotes: '',
        ultrasonicPictures: [],
        // Visual Connection Conditions
        connectionNotes: '',
        connectionPictures: [],
        // NDT of Welds
        ndtPassFail: '', // 'pass' or 'fail'
        ndtNotes: '',
        ndtPictures: [],
        // 3rd Party
        thirdPartyPassFail: '', // 'pass' or 'fail'
        // Signature
        signatureType: 'internal', // 'internal' or 'client'
        internalEmployeeId: userId,
        internalEmployeeName: userFullName,
        internalSignature: '',
        internalSignatureDate: dayjs().format('YYYY-MM-DD'),
        clientName: '',
        clientSignature: '',
        clientSignatureDate: dayjs().format('YYYY-MM-DD'),
        status: STATUS_DRAFT,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [inspectorModalOpen, setInspectorModalOpen] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [cameraTarget, setCameraTarget] = useState(null);

    const saveTimer = useRef(null);
    const sigPadRef = useRef(null);

    // Dictation for notes
    const { isSupported: speechSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({ lang: 'en-US' });
    const [dictationField, setDictationField] = useState(null);

    // Sort jobs by Job number in descending order
    const sortedJobs = useMemo(() => {
        return [...jobs].sort((a, b) => {
            const jobA = parseInt(a.Job) || 0;
            const jobB = parseInt(b.Job) || 0;
            return jobB - jobA;
        });
    }, [jobs]);

    // Load jobs and employees from IndexedDB
    useEffect(() => {
        (async () => {
            try {
                const db = await initializeDB();

                // Load jobs
                if (db.objectStoreNames.contains(JOB_STORE)) {
                    const tx = db.transaction(JOB_STORE, 'readonly');
                    const store = tx.objectStore(JOB_STORE);
                    const allJobs = await store.getAll();
                    const options = (allJobs || [])
                        .filter(j => j && (j.Job || j.JobName))
                        .map(j => ({ ...j, label: `${j.Job || ''} - ${j.JobName || ''}`.trim() }));
                    setJobs(options);
                }

                // Load employees
                if (db.objectStoreNames.contains(EMPLOYEE_STORE)) {
                    const tx2 = db.transaction(EMPLOYEE_STORE, 'readonly');
                    const store2 = tx2.objectStore(EMPLOYEE_STORE);
                    const allEmployees = await store2.getAll();
                    setEmployees(allEmployees || []);
                }
            } catch (e) {
                console.error('Error loading data:', e);
            }
        })();
    }, []);

    // Load existing draft by uuid if provided
    useEffect(() => {
        const uuid = searchParams.get('uuid');
        if (uuid) {
            loadDraft(uuid);
        }
    }, [searchParams]);

    const loadDraft = async (uuid) => {
        try {
            const draft = await gasLineInspectionService.loadByUuid(uuid);
            if (draft) {
                // If form is already submitted, redirect to list
                if (draft.status === STATUS_SUBMITTED) {
                    navigate('/piping/forms/gas-line-inspection/list');
                    return;
                }
                setForm(draft);
                setSubmitted(false);
            }
        } catch (e) {
            console.error('Error loading draft:', e);
        }
    };

    // Auto-save function
    const queueSave = (data) => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            await saveDraft(data);
        }, 500);
    };

    const saveDraft = async (data) => {
        if (submitted) return;
        try {
            const saveData = {
                ...data,
                updatedAt: new Date().toISOString()
            };
            await gasLineInspectionService.saveDraft(saveData);
            console.log('Draft saved:', saveData.uuid);
        } catch (e) {
            console.error('Error saving draft:', e);
        }
    };

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    // Handle job selection
    const handleJobChange = (event, newValue) => {
        if (newValue) {
            const updated = {
                ...form,
                jobCode: newValue.Job || '',
                jobName: newValue.JobName || '',
                customer: newValue.Customer || '',
                location: newValue.location || newValue.Location || ''
            };
            setForm(updated);
            queueSave(updated);
        } else {
            const updated = {
                ...form,
                jobCode: '',
                jobName: '',
                customer: '',
                location: ''
            };
            setForm(updated);
            queueSave(updated);
        }
    };

    // Handle inspector selection
    const handleSelectInspector = (employee) => {
        const updated = {
            ...form,
            inspectorId: employee.id,
            inspectorName: employee.FullName || employee.name || ''
        };
        setForm(updated);
        queueSave(updated);
        setInspectorModalOpen(false);
    };

    // Handle dictation for notes
    const handleStartDictation = (field) => {
        setDictationField(field);
        startDictation({
            onResult: (transcript) => {
                setForm(currentForm => {
                    const currentText = currentForm[field] || '';
                    const needsSpace = currentText && !currentText.endsWith(' ');
                    const newText = currentText + (needsSpace ? ' ' : '') + transcript + ' ';
                    const updated = {
                        ...currentForm,
                        [field]: newText
                    };
                    queueSave(updated);
                    return updated;
                });
            }
        });
    };

    const handleStopDictation = () => {
        stopDictation();
        setDictationField(null);
    };

    // Handle photo capture
    const handleCapturePhoto = (field) => {
        setCameraTarget({ field });
    };

    const handlePhotoCapture = (photoData) => {
        if (cameraTarget && cameraTarget.field) {
            const field = cameraTarget.field;
            const pictures = [...(form[field] || [])];
            pictures.push({
                id: generateUUID(),
                dataUrl: photoData.dataUrl,
                name: photoData.name,
                timestamp: photoData.timestamp
            });
            handleFieldChange(field, pictures);
        }
        setCameraTarget(null);
    };

    const handleRemovePhoto = (field, photoId) => {
        const pictures = form[field]?.filter(p => p.id !== photoId) || [];
        handleFieldChange(field, pictures);
    };

    // Handle signature section
    const handleSignatureTypeChange = (event) => {
        const newType = event.target.value;
        const updated = {
            ...form,
            signatureType: newType
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleSelectEmployee = (employee) => {
        const updated = {
            ...form,
            internalEmployeeId: employee.id,
            internalEmployeeName: employee.FullName || employee.name || ''
        };
        setForm(updated);
        queueSave(updated);
        setEmployeeModalOpen(false);
    };

    const handleSignatureAccept = () => {
        if (!hasSignatureContent(sigPadRef)) {
            return;
        }

        const dataUrl = sigPadRef.current.toDataURL();
        const dateField = form.signatureType === 'internal' ? 'internalSignatureDate' : 'clientSignatureDate';
        const sigField = form.signatureType === 'internal' ? 'internalSignature' : 'clientSignature';
        const updated = {
            ...form,
            [sigField]: dataUrl,
            [dateField]: dayjs().format('YYYY-MM-DD')
        };
        setForm(updated);
        queueSave(updated);
        setSignatureModalOpen(false);
    };

    const handleClearSignature = () => {
        const sigField = form.signatureType === 'internal' ? 'internalSignature' : 'clientSignature';
        const updated = {
            ...form,
            [sigField]: ''
        };
        setForm(updated);
        queueSave(updated);
    };

    // Form validation
    const validate = () => {
        const newErrors = {};
        if (!form.jobCode) newErrors.jobCode = 'Job is required';
        if (!form.testDate) newErrors.testDate = 'Date is required';
        if (!form.inspectorName) newErrors.inspectorName = 'Inspector is required';

        if (form.signatureType === 'internal') {
            if (!form.internalSignature) newErrors.internalSignature = 'Signature is required';
            if (!form.internalEmployeeName) newErrors.internalEmployeeName = 'Employee name is required';
        } else {
            if (!form.clientSignature) newErrors.clientSignature = 'Signature is required';
            if (!form.clientName) newErrors.clientName = 'Client name is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Submit form
    const handleSubmit = async () => {
        if (!validate()) {
            await Swal.fire({
                icon: 'warning',
                title: 'Validation Error',
                text: 'Please fill in all required fields',
                confirmButtonColor: '#ff9800'
            });
            return;
        }

        setSaving(true);
        try {
            const submitData = {
                ...form,
                status: STATUS_SUBMITTED,
                updatedAt: new Date().toISOString()
            };

            // Save to API
            const response = await gasLineInspectionService.submit(submitData);

            if (response.success) {
                // Delete from IndexedDB after successful submission
                await gasLineInspectionService.delete(submitData.uuid);

                setForm(submitData);
                setSubmitted(true);
                setSaving(false);

                const result = await Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Gas Line Inspection form submitted successfully!',
                    confirmButtonColor: '#4caf50'
                });

                if (result.isConfirmed || result.isDismissed) {
                    navigate('/piping/forms/gas-line-inspection/list');
                }
            } else {
                setSaving(false);
                throw new Error(response.message || 'Failed to submit form');
            }
        } catch (e) {
            console.error('Error submitting form:', e);
            setSaving(false);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: e.response?.data?.message || 'Failed to submit form. Please try again.',
                confirmButtonColor: '#f44336'
            });
        }
    };

    const currentSignature = form.signatureType === 'internal' ? form.internalSignature : form.clientSignature;

    return (
        <Box sx={{
            minHeight: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: 'background.paper'
                }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => navigate('/piping/forms/gas-line-inspection/list')} color="primary" size="large">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5">Gas Line Inspection</Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            {form.jobCode ? `Job: ${form.jobCode} - ${form.jobName}` : 'New Inspection Form'}
                        </Typography>
                    </Box>
                </Box>
                <Box display="flex" gap={1}>
                    {form.status === STATUS_DRAFT && (
                        <Chip label="Draft" color="default" size="small" />
                    )}
                    {form.status === STATUS_SUBMITTED && (
                        <Chip label="Submitted" color="success" size="small" />
                    )}
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 1100
                    }}
                >
                    {/* Job Information - Accordion */}
                    <Accordion
                        defaultExpanded
                        elevation={1}
                        sx={{
                            mb: 0.5,
                            mt: 0,
                            '&.Mui-expanded': {
                                margin: 0,
                                mb: 0.5
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '36px !important',
                                height: '36px',
                                py: 0,
                                '& .MuiAccordionSummary-content': {
                                    margin: '6px 0 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Job Information</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Stack spacing={1.5}>
                                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Autocomplete
                                            options={sortedJobs}
                                            getOptionLabel={(option) => option.label || ''}
                                            value={sortedJobs.find(j => j.Job === form.jobCode) || null}
                                            onChange={handleJobChange}
                                            disabled={submitted}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    label="Job *"
                                                    error={!!errors.jobCode}
                                                    helperText={errors.jobCode}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Customer"
                                            value={form.customer}
                                            InputProps={{
                                                readOnly: true
                                            }}
                                            disabled={submitted}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Location"
                                            value={form.location}
                                            InputProps={{
                                                readOnly: true
                                            }}
                                            disabled={submitted}
                                        />
                                    </Grid>
                                </Grid>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Basic Information - Accordion */}
                    <Accordion
                        defaultExpanded
                        elevation={1}
                        sx={{
                            mb: 0.5,
                            mt: 0,
                            '&.Mui-expanded': {
                                margin: 0,
                                mb: 0.5
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '36px !important',
                                height: '36px',
                                py: 0,
                                '& .MuiAccordionSummary-content': {
                                    margin: '6px 0 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Basic Information</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Stack spacing={1.5}>
                                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Date *"
                                            type="date"
                                            value={form.testDate}
                                            onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                            onChange={(e) => handleFieldChange('testDate', e.target.value)}
                                            disabled={submitted}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                style: { cursor: submitted ? 'default' : 'pointer' }
                                            }}
                                            error={!!errors.testDate}
                                            helperText={errors.testDate}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Inspector *"
                                            value={form.inspectorName}
                                            onClick={() => !submitted && setInspectorModalOpen(true)}
                                            InputProps={{
                                                readOnly: true,
                                                style: { cursor: submitted ? 'default' : 'pointer' },
                                                endAdornment: !submitted && (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setInspectorModalOpen(true)} size="small">
                                                            <PersonSearchIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                            error={!!errors.inspectorName}
                                            helperText={errors.inspectorName}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Area"
                                            value={form.area}
                                            onChange={(e) => handleFieldChange('area', e.target.value)}
                                            disabled={submitted}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Column"
                                            value={form.column}
                                            onChange={(e) => handleFieldChange('column', e.target.value)}
                                            disabled={submitted}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Hall"
                                            value={form.hall}
                                            onChange={(e) => handleFieldChange('hall', e.target.value)}
                                            disabled={submitted}
                                        />
                                    </Grid>
                                </Grid>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Visual Inspection - Accordion */}
                    <Accordion
                        elevation={1}
                        sx={{
                            mb: 0.5,
                            mt: 0,
                            '&.Mui-expanded': {
                                margin: 0,
                                mb: 0.5
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '36px !important',
                                height: '36px',
                                py: 0,
                                '& .MuiAccordionSummary-content': {
                                    margin: '6px 0 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Visual Inspection</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Stack spacing={1.5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Notes"
                                    multiline
                                    rows={3}
                                    value={form.visualNotes}
                                    onChange={(e) => handleFieldChange('visualNotes', e.target.value)}
                                    disabled={submitted}
                                    InputProps={{
                                        endAdornment: !submitted && speechSupported && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        dictationField === 'visualNotes'
                                                            ? handleStopDictation()
                                                            : handleStartDictation('visualNotes')
                                                    }
                                                    color={dictationField === 'visualNotes' ? 'error' : 'default'}
                                                >
                                                    {dictationField === 'visualNotes' ? <MicOffIcon /> : <MicIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            Pictures ({form.visualPictures?.length || 0})
                                        </Typography>
                                        {!submitted && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<PhotoCameraIcon />}
                                                onClick={() => handleCapturePhoto('visualPictures')}
                                                sx={{ fontSize: '0.75rem', py: 0.5 }}
                                            >
                                                Add Photo
                                            </Button>
                                        )}
                                    </Box>
                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                        {form.visualPictures?.map((photo) => (
                                            <Box key={photo.id} position="relative" display="inline-block">
                                                <img
                                                    src={photo.dataUrl}
                                                    alt=""
                                                    style={{
                                                        width: 60,
                                                        height: 60,
                                                        objectFit: 'cover',
                                                        borderRadius: 4,
                                                        border: '1px solid #ddd'
                                                    }}
                                                />
                                                {!submitted && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemovePhoto('visualPictures', photo.id)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -6,
                                                            right: -6,
                                                            bgcolor: 'error.main',
                                                            color: 'white',
                                                            p: 0.25,
                                                            '&:hover': { bgcolor: 'error.dark' }
                                                        }}
                                                    >
                                                        <ClearIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Visual Weld Conditions - Accordion */}
                    <Accordion
                        elevation={1}
                        sx={{
                            mb: 0.5,
                            mt: 0,
                            '&.Mui-expanded': {
                                margin: 0,
                                mb: 0.5
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '36px !important',
                                height: '36px',
                                py: 0,
                                '& .MuiAccordionSummary-content': {
                                    margin: '6px 0 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Visual Weld Conditions</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Stack spacing={1.5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Notes"
                                    multiline
                                    rows={3}
                                    value={form.weldNotes}
                                    onChange={(e) => handleFieldChange('weldNotes', e.target.value)}
                                    disabled={submitted}
                                    InputProps={{
                                        endAdornment: !submitted && speechSupported && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        dictationField === 'weldNotes'
                                                            ? handleStopDictation()
                                                            : handleStartDictation('weldNotes')
                                                    }
                                                    color={dictationField === 'weldNotes' ? 'error' : 'default'}
                                                >
                                                    {dictationField === 'weldNotes' ? <MicOffIcon /> : <MicIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            Pictures ({form.weldPictures?.length || 0})
                                        </Typography>
                                        {!submitted && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<PhotoCameraIcon />}
                                                onClick={() => handleCapturePhoto('weldPictures')}
                                                sx={{ fontSize: '0.75rem', py: 0.5 }}
                                            >
                                                Add Photo
                                            </Button>
                                        )}
                                    </Box>
                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                        {form.weldPictures?.map((photo) => (
                                            <Box key={photo.id} position="relative" display="inline-block">
                                                <img
                                                    src={photo.dataUrl}
                                                    alt=""
                                                    style={{
                                                        width: 60,
                                                        height: 60,
                                                        objectFit: 'cover',
                                                        borderRadius: 4,
                                                        border: '1px solid #ddd'
                                                    }}
                                                />
                                                {!submitted && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemovePhoto('weldPictures', photo.id)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -6,
                                                            right: -6,
                                                            bgcolor: 'error.main',
                                                            color: 'white',
                                                            p: 0.25,
                                                            '&:hover': { bgcolor: 'error.dark' }
                                                        }}
                                                    >
                                                        <ClearIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Visual Paint Condition - Accordion */}
                    <Accordion
                        elevation={1}
                        sx={{
                            mb: 0.5,
                            mt: 0,
                            '&.Mui-expanded': {
                                margin: 0,
                                mb: 0.5
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '36px !important',
                                height: '36px',
                                py: 0,
                                '& .MuiAccordionSummary-content': {
                                    margin: '6px 0 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Visual Paint Condition</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Stack spacing={1.5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Notes"
                                    multiline
                                    rows={3}
                                    value={form.paintNotes}
                                    onChange={(e) => handleFieldChange('paintNotes', e.target.value)}
                                    disabled={submitted}
                                    InputProps={{
                                        endAdornment: !submitted && speechSupported && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        dictationField === 'paintNotes'
                                                            ? handleStopDictation()
                                                            : handleStartDictation('paintNotes')
                                                    }
                                                    color={dictationField === 'paintNotes' ? 'error' : 'default'}
                                                >
                                                    {dictationField === 'paintNotes' ? <MicOffIcon /> : <MicIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            Pictures ({form.paintPictures?.length || 0})
                                        </Typography>
                                        {!submitted && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<PhotoCameraIcon />}
                                                onClick={() => handleCapturePhoto('paintPictures')}
                                                sx={{ fontSize: '0.75rem', py: 0.5 }}
                                            >
                                                Add Photo
                                            </Button>
                                        )}
                                    </Box>
                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                        {form.paintPictures?.map((photo) => (
                                            <Box key={photo.id} position="relative" display="inline-block">
                                                <img
                                                    src={photo.dataUrl}
                                                    alt=""
                                                    style={{
                                                        width: 60,
                                                        height: 60,
                                                        objectFit: 'cover',
                                                        borderRadius: 4,
                                                        border: '1px solid #ddd'
                                                    }}
                                                />
                                                {!submitted && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemovePhoto('paintPictures', photo.id)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -6,
                                                            right: -6,
                                                            bgcolor: 'error.main',
                                                            color: 'white',
                                                            p: 0.25,
                                                            '&:hover': { bgcolor: 'error.dark' }
                                                        }}
                                                    >
                                                        <ClearIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Ultrasonic Testing - Accordion */}
                    <Accordion
                        elevation={1}
                        sx={{
                            mb: 0.5,
                            mt: 0,
                            '&.Mui-expanded': {
                                margin: 0,
                                mb: 0.5
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '36px !important',
                                height: '36px',
                                py: 0,
                                '& .MuiAccordionSummary-content': {
                                    margin: '6px 0 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Ultrasonic Testing</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Stack spacing={1.5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Base Material Thickness"
                                    value={form.ultrasonicData}
                                    onChange={(e) => handleFieldChange('ultrasonicData', e.target.value)}
                                    disabled={submitted}
                                    placeholder="e.g., Steel 304, 0.5 inch"
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Notes"
                                    multiline
                                    rows={3}
                                    value={form.ultrasonicNotes}
                                    onChange={(e) => handleFieldChange('ultrasonicNotes', e.target.value)}
                                    disabled={submitted}
                                    InputProps={{
                                        endAdornment: !submitted && speechSupported && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        dictationField === 'ultrasonicNotes'
                                                            ? handleStopDictation()
                                                            : handleStartDictation('ultrasonicNotes')
                                                    }
                                                    color={dictationField === 'ultrasonicNotes' ? 'error' : 'default'}
                                                >
                                                    {dictationField === 'ultrasonicNotes' ? <MicOffIcon /> : <MicIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            Pictures ({form.ultrasonicPictures?.length || 0})
                                        </Typography>
                                        {!submitted && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<PhotoCameraIcon />}
                                                onClick={() => handleCapturePhoto('ultrasonicPictures')}
                                                sx={{ fontSize: '0.75rem', py: 0.5 }}
                                            >
                                                Add Photo
                                            </Button>
                                        )}
                                    </Box>
                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                        {form.ultrasonicPictures?.map((photo) => (
                                            <Box key={photo.id} position="relative" display="inline-block">
                                                <img
                                                    src={photo.dataUrl}
                                                    alt=""
                                                    style={{
                                                        width: 60,
                                                        height: 60,
                                                        objectFit: 'cover',
                                                        borderRadius: 4,
                                                        border: '1px solid #ddd'
                                                    }}
                                                />
                                                {!submitted && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemovePhoto('ultrasonicPictures', photo.id)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -6,
                                                            right: -6,
                                                            bgcolor: 'error.main',
                                                            color: 'white',
                                                            p: 0.25,
                                                            '&:hover': { bgcolor: 'error.dark' }
                                                        }}
                                                    >
                                                        <ClearIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Visual Connection Conditions - Accordion */}
                    <Accordion
                        elevation={1}
                        sx={{
                            mb: 0.5,
                            mt: 0,
                            '&.Mui-expanded': {
                                margin: 0,
                                mb: 0.5
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '36px !important',
                                height: '36px',
                                py: 0,
                                '& .MuiAccordionSummary-content': {
                                    margin: '6px 0 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Visual Connection Conditions</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Stack spacing={1.5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Notes"
                                    multiline
                                    rows={3}
                                    value={form.connectionNotes}
                                    onChange={(e) => handleFieldChange('connectionNotes', e.target.value)}
                                    disabled={submitted}
                                    InputProps={{
                                        endAdornment: !submitted && speechSupported && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        dictationField === 'connectionNotes'
                                                            ? handleStopDictation()
                                                            : handleStartDictation('connectionNotes')
                                                    }
                                                    color={dictationField === 'connectionNotes' ? 'error' : 'default'}
                                                >
                                                    {dictationField === 'connectionNotes' ? <MicOffIcon /> : <MicIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            Pictures ({form.connectionPictures?.length || 0})
                                        </Typography>
                                        {!submitted && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<PhotoCameraIcon />}
                                                onClick={() => handleCapturePhoto('connectionPictures')}
                                                sx={{ fontSize: '0.75rem', py: 0.5 }}
                                            >
                                                Add Photo
                                            </Button>
                                        )}
                                    </Box>
                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                        {form.connectionPictures?.map((photo) => (
                                            <Box key={photo.id} position="relative" display="inline-block">
                                                <img
                                                    src={photo.dataUrl}
                                                    alt=""
                                                    style={{
                                                        width: 60,
                                                        height: 60,
                                                        objectFit: 'cover',
                                                        borderRadius: 4,
                                                        border: '1px solid #ddd'
                                                    }}
                                                />
                                                {!submitted && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemovePhoto('connectionPictures', photo.id)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -6,
                                                            right: -6,
                                                            bgcolor: 'error.main',
                                                            color: 'white',
                                                            p: 0.25,
                                                            '&:hover': { bgcolor: 'error.dark' }
                                                        }}
                                                    >
                                                        <ClearIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* NDT of Welds - Accordion */}
                    <Accordion
                        elevation={1}
                        sx={{
                            mb: 0.5,
                            mt: 0,
                            '&.Mui-expanded': {
                                margin: 0,
                                mb: 0.5
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '36px !important',
                                height: '36px',
                                py: 0,
                                '& .MuiAccordionSummary-content': {
                                    margin: '6px 0 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>NON-Destructive Testing</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Stack spacing={1.5}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                            NDT Result
                                        </Typography>
                                        <ToggleButtonGroup
                                            color="primary"
                                            value={form.ndtPassFail}
                                            exclusive
                                            onChange={(e, newValue) => newValue && handleFieldChange('ndtPassFail', newValue)}
                                            disabled={submitted}
                                            size="small"
                                            fullWidth
                                        >
                                            <ToggleButton value="pass" sx={{ px: 3 }}>
                                                Pass
                                            </ToggleButton>
                                            <ToggleButton value="fail" sx={{ px: 3 }}>
                                                Fail
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                            3rd Party Result
                                        </Typography>
                                        <ToggleButtonGroup
                                            color="primary"
                                            value={form.thirdPartyPassFail}
                                            exclusive
                                            onChange={(e, newValue) => newValue && handleFieldChange('thirdPartyPassFail', newValue)}
                                            disabled={submitted}
                                            size="small"
                                            fullWidth
                                        >
                                            <ToggleButton value="pass" sx={{ px: 3 }}>
                                                Pass
                                            </ToggleButton>
                                            <ToggleButton value="fail" sx={{ px: 3 }}>
                                                Fail
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Grid>
                                </Grid>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Notes"
                                    multiline
                                    rows={3}
                                    value={form.ndtNotes}
                                    onChange={(e) => handleFieldChange('ndtNotes', e.target.value)}
                                    disabled={submitted}
                                    InputProps={{
                                        endAdornment: !submitted && speechSupported && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        dictationField === 'ndtNotes'
                                                            ? handleStopDictation()
                                                            : handleStartDictation('ndtNotes')
                                                    }
                                                    color={dictationField === 'ndtNotes' ? 'error' : 'default'}
                                                >
                                                    {dictationField === 'ndtNotes' ? <MicOffIcon /> : <MicIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            Pictures ({form.ndtPictures?.length || 0})
                                        </Typography>
                                        {!submitted && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<PhotoCameraIcon />}
                                                onClick={() => handleCapturePhoto('ndtPictures')}
                                                sx={{ fontSize: '0.75rem', py: 0.5 }}
                                            >
                                                Add Photo
                                            </Button>
                                        )}
                                    </Box>
                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                        {form.ndtPictures?.map((photo) => (
                                            <Box key={photo.id} position="relative" display="inline-block">
                                                <img
                                                    src={photo.dataUrl}
                                                    alt=""
                                                    style={{
                                                        width: 60,
                                                        height: 60,
                                                        objectFit: 'cover',
                                                        borderRadius: 4,
                                                        border: '1px solid #ddd'
                                                    }}
                                                />
                                                {!submitted && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemovePhoto('ndtPictures', photo.id)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -6,
                                                            right: -6,
                                                            bgcolor: 'error.main',
                                                            color: 'white',
                                                            p: 0.25,
                                                            '&:hover': { bgcolor: 'error.dark' }
                                                        }}
                                                    >
                                                        <ClearIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Signature Section */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1.5} mb={1.25}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Signature</Typography>
                                <ToggleButtonGroup
                                    color="primary"
                                    value={form.signatureType}
                                    exclusive
                                    onChange={(e, newValue) => newValue && handleSignatureTypeChange({ target: { value: newValue } })}
                                    disabled={submitted}
                                    size="small"
                                >
                                    <ToggleButton value="internal" sx={{ px: 2 }}>
                                        Internal
                                    </ToggleButton>
                                    <ToggleButton value="client" sx={{ px: 2 }}>
                                        Client/Inspector
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {form.signatureType === 'internal' ? (
                                <Box>
                                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Employee Name *"
                                                value={form.internalEmployeeName}
                                                onClick={() => !submitted && setEmployeeModalOpen(true)}
                                                InputProps={{
                                                    readOnly: true,
                                                    style: { cursor: submitted ? 'default' : 'pointer' },
                                                    endAdornment: !submitted && (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={() => setEmployeeModalOpen(true)} size="small">
                                                                <PersonSearchIcon fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                                error={!!errors.internalEmployeeName}
                                                helperText={errors.internalEmployeeName}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Date"
                                                type="date"
                                                value={form.internalSignatureDate}
                                                onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                                onChange={(e) => handleFieldChange('internalSignatureDate', e.target.value)}
                                                disabled={submitted}
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{
                                                    style: { cursor: submitted ? 'default' : 'pointer' }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box display="flex" justifyContent="center" alignItems="center">
                                                {form.internalSignature ? (
                                                    <Box position="relative" display="inline-block">
                                                        <img
                                                            src={form.internalSignature}
                                                            alt="Signature"
                                                            style={{
                                                                maxWidth: 200,
                                                                height: 60,
                                                                border: '1px solid #ddd',
                                                                borderRadius: 4,
                                                                cursor: submitted ? 'default' : 'pointer'
                                                            }}
                                                            onClick={() => !submitted && setSignatureModalOpen(true)}
                                                            title={submitted ? '' : 'Click to edit signature'}
                                                        />
                                                        {!submitted && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleClearSignature}
                                                                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                            >
                                                                <ClearIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<DrawIcon />}
                                                        onClick={() => setSignatureModalOpen(true)}
                                                        disabled={submitted}
                                                    >
                                                        Add Signature
                                                    </Button>
                                                )}
                                            </Box>
                                            {errors.internalSignature && (
                                                <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                                    {errors.internalSignature}
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Box>
                            ) : (
                                <Box>
                                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Client/Inspector Name *"
                                                value={form.clientName}
                                                onChange={(e) => handleFieldChange('clientName', e.target.value)}
                                                disabled={submitted}
                                                error={!!errors.clientName}
                                                helperText={errors.clientName}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Date"
                                                type="date"
                                                value={form.clientSignatureDate}
                                                onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                                onChange={(e) => handleFieldChange('clientSignatureDate', e.target.value)}
                                                disabled={submitted}
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{
                                                    style: { cursor: submitted ? 'default' : 'pointer' }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box display="flex" justifyContent="center" alignItems="center">
                                                {form.clientSignature ? (
                                                    <Box position="relative" display="inline-block">
                                                        <img
                                                            src={form.clientSignature}
                                                            alt="Signature"
                                                            style={{
                                                                maxWidth: 200,
                                                                height: 60,
                                                                border: '1px solid #ddd',
                                                                borderRadius: 4,
                                                                cursor: submitted ? 'default' : 'pointer'
                                                            }}
                                                            onClick={() => !submitted && setSignatureModalOpen(true)}
                                                            title={submitted ? '' : 'Click to edit signature'}
                                                        />
                                                        {!submitted && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleClearSignature}
                                                                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                            >
                                                                <ClearIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<DrawIcon />}
                                                        onClick={() => setSignatureModalOpen(true)}
                                                        disabled={submitted}
                                                    >
                                                        Add Signature
                                                    </Button>
                                                )}
                                            </Box>
                                            {errors.clientSignature && (
                                                <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                                    {errors.clientSignature}
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Submit Button */}
                    {!submitted && (
                        <Box display="flex" justifyContent="center" sx={{ mt: 2, mb: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleSubmit}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={20} /> : <SendIcon />}
                                sx={{ minWidth: 200 }}
                            >
                                {saving ? 'Submitting...' : 'Submit'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Modals */}
            <SelectSingleEmployeeModal
                open={inspectorModalOpen}
                onClose={() => setInspectorModalOpen(false)}
                onSelect={handleSelectInspector}
                employees={employees}
            />

            <SelectSingleEmployeeModal
                open={employeeModalOpen}
                onClose={() => setEmployeeModalOpen(false)}
                onSelect={handleSelectEmployee}
                employees={employees}
            />

            <SignatureModalWithAuto
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                sigPadRef={sigPadRef}
                employeeName={form.signatureType === 'internal' ? form.internalEmployeeName : form.clientName}
                onAccept={handleSignatureAccept}
                existingSignature={form.signatureType === 'internal' ? form.internalSignature : form.clientSignature}
            />

            <CameraCapture
                open={!!cameraTarget}
                onClose={() => setCameraTarget(null)}
                onCapture={handlePhotoCapture}
            />

            <Backdrop open={saving} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
}
